import { useEffect, useMemo, useState } from "react";
import EntriesTable, { ColumnWidths, SortingMode } from "../components/EntriesTable/EntriesTable";
import { useDashboardData, Entry } from "@/shared/contexts/DashboardDataContext";

type Props = {
  date: Date;
  sortingMode: SortingMode;
  onToggleSortingMode?: () => void;
  filterEntries?: <T extends { application?: string }>(entries: T[]) => T[];
};

export default function HistoryView({ date, sortingMode, onToggleSortingMode, filterEntries }: Props) {
  const { exportedEntries } = useDashboardData();
  const [groupedEntries, setGroupedEntries] = useState<Record<string, Entry[]>>({});

  const defaultColumnWidths: ColumnWidths = { date: 7, matter: 25, codes: 7, narrative: 56, units: 10 };
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>(() => {
    try {
      const saved = localStorage.getItem("dashboard-column-widths");
      if (!saved) return defaultColumnWidths;
      const parsed = JSON.parse(saved);
      // Migrate legacy keys: task->codes, description->narrative
      const migrated: Partial<ColumnWidths> = { ...parsed };
      if (parsed.task && !parsed.codes) migrated.codes = parsed.task;
      if (parsed.description && !parsed.narrative) migrated.narrative = parsed.description;
      // Remove legacy keys that are no longer used
      delete (migrated as any).title;
      delete (migrated as any).description;
      delete (migrated as any).task;
      return { ...defaultColumnWidths, ...migrated } as ColumnWidths;
    } catch {
      return defaultColumnWidths;
    }
  });

  useEffect(() => {
    try { localStorage.setItem("dashboard-column-widths", JSON.stringify(columnWidths)); } catch {}
  }, [columnWidths]);

  const filteredEntries = useMemo(() => {
    // First filter by date
    const dateFiltered = exportedEntries.filter((e) => {
      const d = new Date(e.date);
      return d.getFullYear() === date.getFullYear() && d.getMonth() === date.getMonth() && d.getDate() === date.getDate();
    });
    // Then apply application filter if provided
    return filterEntries ? filterEntries(dateFiltered) : dateFiltered;
  }, [exportedEntries, date, filterEntries]);

  useEffect(() => {
    const groups: Record<string, Entry[]> = {};
    if (sortingMode === "matter") {
      const unsorted: Record<string, Entry[]> = {};
      filteredEntries.forEach((row) => {
        const key = row.matter || "Unknown";
        if (!unsorted[key]) unsorted[key] = [];
        unsorted[key].push(row);
      });
      // Sort group keys alphabetically so order is stable across re-fetches
      Object.keys(unsorted).sort((a, b) => a.localeCompare(b)).forEach((key) => {
        groups[key] = unsorted[key];
      });
    } else {
      filteredEntries.forEach((row) => {
        let hourLabel = "";
        if (row.end_time) {
          const dateObj = new Date(row.end_time);
          // Backend sends local time with Z suffix, so use getUTCHours to avoid double conversion
          const hours = dateObj.getUTCHours();
          const period = hours >= 12 ? "PM" : "AM";
          const displayHour = hours % 12 || 12;
          hourLabel = `${displayHour}:00 ${period}`;
        }
        const key = hourLabel || "Unknown";
        if (!groups[key]) groups[key] = [];
        groups[key].push(row);
      });
    }
    setGroupedEntries(groups);
  }, [filteredEntries, sortingMode]);

  return (
    <>
      <EntriesTable
        entries={filteredEntries}
        groupedEntries={groupedEntries}
        columnWidths={columnWidths}
        onResizeColumn={(key, w) => setColumnWidths((prev) => ({ ...prev, [key]: w }))}
        checkedRows={new Set()}
        selectAllChecked={false}
        selectAllIndeterminate={false}
        onToggleSelectAll={() => {}}
        onToggleRow={() => {}}
        onToggleSortingMode={onToggleSortingMode}
        showCheckboxes={false}
        mode="history"
        sortingMode={sortingMode}
      />
    </>
  );
}
